import {
	DEFAULT_LAYER_MODE,
	type ISpreadingCompaction,
	type ISpreadingCompactionDraft,
	type ISpreadingCompactionInput,
	type ITarget,
	type ITerrain,
	type TOffsetType,
} from "@/store/modules/spreading-compaction-types";
import { notifyError, notifySuccess } from "@/utils/notificationUtils";
import { computed, reactive } from "vue";
import { useI18n } from "vue-i18n";
import { useStore } from "vuex";
import {
	buildOffsetInitialVisibility,
	buildTargetItems,
	buildTerrainItems,
	collectOffsets,
	hasCompactionOutline,
	isOffsetVisible,
	parseTargetSelection,
	parseTerrainSelection,
	resolveAssociatedTerrainValue,
	withSelectedTargetItem,
} from "./spreadingCompactionHelpers";
import {
	SpreadingCompactionAlreadyRunningError,
	type StartSpreadingCompactionResult,
	applyLayerModeToFaceSelection,
	clearSpreadingCompactionResult,
	deleteSpreadingCompactionApi,
	disposeViewerEntry,
	exportSpreadingCompaction,
	exportSpreadingCompactionToGroupware,
	refreshCrossSection,
	setAllResultsVisible,
	setFaceSelectionAngleTolerance,
	startFaceSelection,
	startSpreadingCompactionApi,
	stopFaceSelection,
	syncViewerWithEntry,
	teardownViewerState,
	trackSpreadingCompactionJob,
	updateViewerOffsetVisible,
	updateViewerTargetOpacity,
} from "./spreadingCompactionService";
import { useSpreadingCompactionUnit } from "./useSpreadingCompactionUnit";

type TFormState = ISpreadingCompaction | ISpreadingCompactionDraft;
type TUpdatePayload = Partial<
	Pick<
		TFormState,
		| "name"
		| "target"
		| "terrain"
		| "compactionSurfaceSpacing"
		| "slopeShoulderOffset"
		| "layerMode"
	>
>;

/** Draft parameters in mm, derived from the site unit's display defaults. */
type DraftDefaultsMm = {
	compactionSurfaceSpacing: number;
	slopeShoulderOffset: number;
};

function emptyDraft(
	name: string,
	defaults: Readonly<DraftDefaultsMm>,
): ISpreadingCompactionDraft {
	return {
		name,
		target: null,
		terrain: null,
		compactionSurfaceSpacing: defaults.compactionSurfaceSpacing,
		slopeShoulderOffset: defaults.slopeShoulderOffset,
		layerMode: DEFAULT_LAYER_MODE,
		targetVisible: true,
		targetOpacity: 0.7,
	};
}

export function useSpreadingCompaction() {
	const store = useStore();
	const { t } = useI18n();
	const unitContext = useSpreadingCompactionUnit();

	const spreadingCompactions = computed(
		() =>
			store.getters[
				"spreadingCompaction/spreadingCompactions"
			] as ISpreadingCompaction[],
	);
	const selectedFromStore = computed(
		() =>
			store.getters[
				"spreadingCompaction/selected"
			] as ISpreadingCompaction | null,
	);
	const draft = computed(
		() =>
			store.getters[
				"spreadingCompaction/draft"
			] as ISpreadingCompactionDraft | null,
	);
	const selectedId = computed(
		() => store.getters["spreadingCompaction/selectedId"] as number | null,
	);
	const isActive = computed(
		() => store.getters["spreadingCompaction/isActive"] as boolean,
	);
	const isProcessing = computed(
		() => store.getters["spreadingCompaction/isProcessing"] as boolean,
	);
	const executionError = computed(
		() => store.getters["spreadingCompaction/executionError"] as string | null,
	);
	const hasResult = computed(
		() => store.getters["spreadingCompaction/hasResult"] as boolean,
	);
	const resultSurfaces = computed(
		() =>
			store.getters[
				"spreadingCompaction/resultSurfaces"
			] as ISpreadingCompactionInput["surfaces"],
	);

	// --- Active entity = draft if present, else selected from store ---
	const selected = computed<TFormState | null>(
		() => draft.value ?? selectedFromStore.value,
	);
	const isDraftMode = computed(() => draft.value !== null);

	const faceSelection = reactive({
		isActive: false,
		selectedCount: 0,
		seedCount: 0,
		excludedCount: 0,
	});
	const isFaceSelectionActive = computed(() => faceSelection.isActive);

	const targetItems = computed(() =>
		withSelectedTargetItem(
			buildTargetItems(
				(store.state.object?.objects ?? []) as Parameters<
					typeof buildTargetItems
				>[0],
				(store.state.design_list ?? []) as Parameters<
					typeof buildTargetItems
				>[1],
				t,
			),
			selected.value?.target,
			t,
		),
	);
	const pointClouds = computed(
		() =>
			(store.state.point_cloud_list ?? []) as Parameters<
				typeof buildTerrainItems
			>[0],
	);
	const terrainItems = computed(() => buildTerrainItems(pointClouds.value));
	/**
	 * Derived from the candidate list rather than from the stored value, so a
	 * deleted point cloud invalidates the selection with no state to sync.
	 */
	const isTerrainValid = computed(() => {
		const terrain = selected.value?.terrain;
		if (!terrain) return false;
		return terrainItems.value.some(
			(item) => item.value === `${terrain.type}:${terrain.id}`,
		);
	});
	const spreadingOffsets = computed(() =>
		collectOffsets(resultSurfaces.value ?? [], "spreading"),
	);
	const compactionOffsets = computed(() =>
		collectOffsets(resultSurfaces.value ?? [], "compaction"),
	);
	/** false なら転圧 CSV は出ない (旧レコード)。再実行で解消する */
	const hasCompactionOutlineResult = computed(() =>
		hasCompactionOutline(selectedFromStore.value),
	);

	function getOffsetVisible(type: TOffsetType, offset: number): boolean {
		return isOffsetVisible(selectedFromStore.value, type, offset);
	}

	function formatOffsetLabel(offsetMm: number, decimals?: number): string {
		return unitContext.formatOffsetLabel(offsetMm, decimals);
	}

	function commit(type: string, payload?: unknown) {
		store.commit(`spreadingCompaction/${type}`, payload);
	}

	function findById(id: number) {
		return spreadingCompactions.value.find((e) => e.id === id);
	}

	function resetFaceSelection() {
		if (faceSelection.isActive) {
			stopFaceSelection(store.state);
		}
		faceSelection.isActive = false;
		faceSelection.selectedCount = 0;
		faceSelection.seedCount = 0;
		faceSelection.excludedCount = 0;
	}

	function teardownCurrent() {
		const current = selectedFromStore.value;
		if (!current) {
			resetFaceSelection();
			return;
		}
		teardownViewerState(current, store.state);
		resetFaceSelection();
	}

	// Defaults are defined in the display unit, so convert them here instead of
	// using the mm constants — 1 ft is 304.8 mm, not 300 mm.
	const draftDefaultsMm = computed<DraftDefaultsMm>(() => ({
		compactionSurfaceSpacing: unitContext.convertDisplayToMm(
			unitContext.unitConfig.value.defaultSpacingDisplay,
		),
		// UI default is "inward" as a positive value; internal mm keeps inward negative.
		slopeShoulderOffset: -unitContext.convertDisplayToMm(
			unitContext.unitConfig.value.defaultSlopeShoulderOffsetDisplay,
		),
	}));

	function create(name?: string) {
		teardownCurrent();
		commit("SELECT", null);
		commit(
			"SET_DRAFT",
			emptyDraft(name ?? t("SPREADING_AND_COMPACTION"), draftDefaultsMm.value),
		);
		commit("SET_ACTIVE", true);
	}

	function cancelDraft() {
		commit("SET_DRAFT", null);
		resetFaceSelection();
	}

	async function remove(id: number) {
		const wasSelected = selectedId.value === id;
		try {
			await deleteSpreadingCompactionApi(id, store.state);
		} catch (e) {
			console.warn("[spreading-compaction] delete API failed:", e);
			return;
		}
		if (wasSelected) {
			teardownCurrent();
		}
		clearSpreadingCompactionResult(id, store.state);
		commit("REMOVE", id);
		if (wasSelected) {
			await syncViewerWithEntry(
				selectedFromStore.value ?? undefined,
				store.state,
			);
		}
	}

	async function select(id: number | null) {
		cancelDraft();
		if (selectedId.value === id) {
			commit("SET_ACTIVE", true);
			return;
		}
		resetFaceSelection();

		// Hide previously-selected entry — only one SC visible at a time.
		const prevId = selectedId.value;
		if (prevId !== null) {
			const prev = findById(prevId);
			if (prev?.execution.status === "success" && prev.visible) {
				commit("SET_VISIBLE", { id: prev.id, visible: false });
				setAllResultsVisible(prev.id, store.state, false);
			}
		}

		commit("SELECT", id);

		const next = id !== null ? findById(id) : undefined;
		if (next?.execution.status === "success") {
			if (!next.visible) commit("SET_VISIBLE", { id: next.id, visible: true });
			// syncViewerWithEntry pushes the (now visible=true) flag to the viewer.
			await syncViewerWithEntry(next, store.state);
		}
		commit("SET_ACTIVE", true);
		syncLayerModeToViewer();
	}

	function update(payload: TUpdatePayload) {
		if (draft.value) {
			commit("UPDATE_DRAFT", payload);
		} else {
			const persisted = selectedFromStore.value;
			if (!persisted) return;
			commit("UPDATE", { id: persisted.id, ...payload });
		}
		if ("layerMode" in payload) syncLayerModeToViewer();
	}

	/** 選択中エンティティのレイヤモードを viewer の面選択に反映する (viewer 未初期化は無視) */
	function syncLayerModeToViewer() {
		try {
			applyLayerModeToFaceSelection(
				store.state,
				selected.value?.layerMode ?? DEFAULT_LAYER_MODE,
			);
		} catch (error: unknown) {
			console.warn("[spreading-compaction] layer mode sync failed:", error);
		}
	}

	function setActive(active: boolean) {
		commit("SET_ACTIVE", active);
	}

	async function executeSelected() {
		// A draft has no `execution`, so the per-entry status cannot express
		// "running" on the first execution. Hence a flag on the module state.
		// The flag only covers the (fast) job start; the long calculation is
		// tracked per entry so the UI stays usable while it runs.
		commit("SET_DRAFT_ERROR", null);
		commit("SET_EXECUTING", true);
		try {
			if (draft.value) {
				await executeDraft(draft.value);
			} else if (selectedFromStore.value) {
				await executeExisting(selectedFromStore.value);
			}
		} finally {
			commit("SET_EXECUTING", false);
		}
	}

	function jobTrackerStore() {
		return { commit, getEntry: findById, rootState: store.state };
	}

	/**
	 * Start the job (202) and add the entry to the list as "processing" right
	 * away — like an uploading asset. The result arrives via polling.
	 */
	async function executeDraft(draft: ISpreadingCompactionDraft) {
		// Second line of defence behind the Execute button's enablement.
		const { target, terrain } = draft;
		if (!target || !terrain) return;
		const request = { ...draft, target, terrain };
		let started: StartSpreadingCompactionResult | undefined;
		try {
			// executeWithSpinner resolves with void, so capture the result in the closure.
			await store.dispatch("executeWithSpinner", async () => {
				started = await startSpreadingCompactionApi(
					request,
					undefined,
					store.state,
				);
			});
		} catch (error: unknown) {
			const message = error instanceof Error ? error.message : "Unknown error";
			commit("SET_DRAFT_ERROR", message);
			console.error("[spreading-compaction] execute (draft) failed:", error);
			return;
		}
		if (!started) return;
		const newEntity: ISpreadingCompaction = {
			id: started.id,
			name: draft.name,
			target,
			terrain,
			compactionSurfaceSpacing: draft.compactionSurfaceSpacing,
			slopeShoulderOffset: draft.slopeShoulderOffset,
			layerMode: draft.layerMode,
			execution: { status: "processing" },
			targetVisible: draft.targetVisible,
			targetOpacity: draft.targetOpacity,
			visible: true,
			offsetVisibility: [],
		};
		commit("ADD", newEntity);
		commit("SELECT", started.id);
		commit("SET_DRAFT", null);
		resetFaceSelection();
		await trackSpreadingCompactionJob(started.id, jobTrackerStore());
	}

	async function executeExisting(entity: ISpreadingCompaction) {
		// Second line of defence behind the Execute button's enablement.
		const { target, terrain } = entity;
		if (!target || !terrain) return;
		// A job is already running for this row; a second one would race it.
		if (entity.execution.status === "processing") return;
		const request = { ...entity, target, terrain };
		commit("SET_EXECUTION", {
			id: entity.id,
			execution: { status: "processing" },
		});
		let started: StartSpreadingCompactionResult | undefined;
		try {
			// executeWithSpinner resolves with void, so capture the result in the closure.
			await store.dispatch("executeWithSpinner", async () => {
				started = await startSpreadingCompactionApi(
					request,
					entity.id,
					store.state,
				);
			});
		} catch (error: unknown) {
			if (error instanceof SpreadingCompactionAlreadyRunningError) {
				// Another client started it; the entry is processing for real — follow that run.
				await trackSpreadingCompactionJob(entity.id, jobTrackerStore());
				return;
			}
			const message = error instanceof Error ? error.message : "Unknown error";
			commit("SET_EXECUTION", {
				id: entity.id,
				execution: { status: "error", error: message },
			});
			console.error("[spreading-compaction] execute (existing) failed:", error);
			return;
		}
		if (!started) return;
		resetFaceSelection();
		await trackSpreadingCompactionJob(started.id, jobTrackerStore());
	}

	async function enterFaceSelectionMode() {
		const current = selected.value;
		if (!current?.target) return;
		syncLayerModeToViewer();

		await startFaceSelection(current, store.state, {
			onStart: () => {
				faceSelection.isActive = true;
			},
			onProgress: (selectedFaces, seeds, excluded) => {
				faceSelection.seedCount = seeds.size;
				faceSelection.excludedCount = excluded.size;
				faceSelection.selectedCount = selectedFaces.size;
			},
		});
	}

	function exitFaceSelectionMode() {
		resetFaceSelection();
	}

	function updateFaceSelectionTolerance(degrees: number) {
		setFaceSelectionAngleTolerance(store.state, degrees);
	}

	function setTargetOpacity(opacity: number) {
		const persisted = selectedFromStore.value;
		if (persisted) {
			updateViewerTargetOpacity(persisted.id, store.state, opacity);
			commit("SET_TARGET_OPACITY", { id: persisted.id, opacity });
			return;
		}
		if (draft.value) commit("UPDATE_DRAFT", { targetOpacity: opacity });
	}

	function setOffsetVisible(payload: {
		type: TOffsetType;
		offset: number;
		visible: boolean;
	}) {
		const persisted = selectedFromStore.value;
		if (!persisted) return;
		updateViewerOffsetVisible(persisted.id, store.state, payload);
		commit("SET_OFFSET_VISIBLE", { id: persisted.id, ...payload });
	}

	/**
	 * Design has no source for an initial value, so a still-valid selection is
	 * kept instead of being cleared. Object always overwrites.
	 */
	function resolveTerrainForTarget(target: ITarget | null): ITerrain | null {
		if (!target) return null;
		if (target.type === "design") {
			return isTerrainValid.value ? (selected.value?.terrain ?? null) : null;
		}
		const objects = (store.state.object?.objects ?? []) as ReadonlyArray<{
			id: string | number;
			associatedTerrain?: { id: number; terrainType: string };
		}>;
		const obj = objects.find((o) => String(o.id) === String(target.id));
		const value = resolveAssociatedTerrainValue(
			obj?.associatedTerrain,
			pointClouds.value,
		);
		return parseTerrainSelection(value, terrainItems.value);
	}

	function setTargetSelection(value: string | null) {
		const target = parseTargetSelection(value, targetItems.value);
		resetFaceSelection();
		update({ target, terrain: resolveTerrainForTarget(target) });
	}

	function setTerrainSelection(value: string | null) {
		update({ terrain: parseTerrainSelection(value, terrainItems.value) });
	}

	async function exportSelected() {
		const persisted = selectedFromStore.value;
		if (persisted?.execution.status !== "success") return;

		await store.dispatch("executeWithSpinner", () =>
			exportSpreadingCompaction(persisted, store.state),
		);
	}

	async function exportById(id: number) {
		const target = findById(id);
		if (target?.execution.status !== "success") return;

		await store.dispatch("executeWithSpinner", () =>
			exportSpreadingCompaction(target, store.state),
		);
	}

	async function exportToGroupwareById(id: number) {
		const target = findById(id);
		if (target?.execution.status !== "success") return;

		try {
			await store.dispatch("executeWithSpinner", () =>
				exportSpreadingCompactionToGroupware(target, store.state),
			);
			notifySuccess({ $store: store }, t("SAVE_TO_GROUPWARE_SUCCESS"));
		} catch (e: unknown) {
			console.error(e);
			notifyError({ $store: store }, t("SAVE_TO_GROUPWARE_FAILED"));
		}
	}

	async function toggleVisible(id: number) {
		const persisted = findById(id);
		if (persisted?.execution.status !== "success") return;
		const next = !persisted.visible;
		commit("SET_VISIBLE", { id, visible: next });

		if (next) {
			await syncViewerWithEntry(persisted, store.state);
		} else {
			setAllResultsVisible(id, store.state, false);
		}
	}

	async function refreshCrossSectionView() {
		await refreshCrossSection(store.state);
	}

	return {
		spreadingCompactions,
		selected,
		selectedId,
		isDraftMode,
		isActive,
		isProcessing,
		executionError,
		hasResult,
		resultSurfaces,
		faceSelection,
		isFaceSelectionActive,
		targetItems,
		terrainItems,
		isTerrainValid,
		spreadingOffsets,
		compactionOffsets,
		hasCompactionOutlineResult,
		getOffsetVisible,
		formatOffsetLabel,
		toggleVisible,
		create,
		cancelDraft,
		remove,
		select,
		update,
		setActive,
		executeSelected,
		enterFaceSelectionMode,
		exitFaceSelectionMode,
		updateFaceSelectionTolerance,
		setTargetOpacity,
		setOffsetVisible,
		refreshCrossSectionView,
		setTargetSelection,
		setTerrainSelection,
		exportSelected,
		exportById,
		exportToGroupwareById,
	};
}
